/**
 * Internal base directive for Harmony button components
 *
 * Following Angular Material's MatButtonBase pattern, this base directive provides shared logic for all button variants
 * (HxButton, future HxFabButton, etc.)
 *
 * **NOT exported publicly** - only HxButton is exported from public-api.ts
 *
 * @internal
 *
 * ## Features
 * - Focus management via CDK FocusMonitor
 * - Click event blocking when disabled or loading
 * - ARIA attribute management (aria-busy, aria-disabled, aria-label)
 * - Keyboard accessibility (Tab navigation, disabled state handling)
 *
 * ## Host Bindings
 * - `class`: 'hx-button-base' (base styling class)
 * - `[attr.disabled]`: Set when disabled() is true
 * - `[attr.aria-busy]`: Set to 'true' when loading() is true
 * - `[attr.aria-label]`: Bound to ariaLabel() input
 * - `[attr.tabindex]`: Set to '-1' when disabled or loading
 * - `(click)`: Click handler that blocks when disabled/loading
 *
 * ## Remarks
 * - Moon Design System requires a distinct loading state for buttons that prevents interaction
 *   and conveys busy status. This base implements `aria-busy="true"`, removes the element from
 *   tab order (`tabindex="-1"`), and blocks click events while loading.
 * - Angular Material's `MatButton` does not natively provide a loading spinner/state; the common
 *   pattern is to disable the button during async work and optionally compose with `MatProgressSpinner`.
 *   Harmony's `hxButton` offers a first-class loading state aligned with Moon DS.
 */
import { FocusMonitor } from '@angular/cdk/a11y';
import { AfterViewInit, booleanAttribute, Directive, ElementRef, inject, input, OnDestroy } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';

@Directive({
  host: {
    class: 'hx-button-base',
    '[attr.disabled]': '_getDisabledAttribute()',
    '[attr.aria-disabled]': '_getAriaDisabled()',
    '[attr.aria-busy]': 'loading() ? "true" : null',
    '[attr.aria-label]': 'ariaLabel()',
    '[attr.tabindex]': '_getTabIndex()',
    '[class.hx-button--disabled]': 'disabledState()',
    '[class.hx-button--loading]': 'loading()',
    '(click)': '_handleClick($event)',
  },
})
export class HxButtonBase extends HxBaseControl implements AfterViewInit, OnDestroy {
  /**
   * Shows loading spinner and prevents interaction Sets aria-busy="true" for accessibility
   *
   * @default false
   */
  readonly loading = input(false, { transform: booleanAttribute });

  // Injected dependencies
  protected _elementRef = inject(ElementRef);
  protected _focusMonitor = inject(FocusMonitor);
  protected _isAnchor: boolean;

  constructor() {
    super();
    const element = this._elementRef.nativeElement;
    this._isAnchor = element.tagName === 'A';
  }

  ngAfterViewInit(): void {
    // Start monitoring focus for accessibility
    this._focusMonitor.monitor(this._elementRef, true);
  }

  ngOnDestroy(): void {
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  protected _handleClick(event: Event): void {
    // Block clicks when disabled or loading
    if (this.disabledState() || this.loading()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  protected _getDisabledAttribute(): boolean | null {
    return !this.disabledState() ? null : true;
  }

  protected _getAriaDisabled(): boolean | null {
    if (this._isAnchor) {
      return this.disabledState() || null;
    }
    return null;
  }

  protected _getTabIndex(): number {
    if (this.disabledState() || this.loading()) {
      return -1;
    }
    return this.tabIndex();
  }

  /** Focuses the button. */
  focus(origin: 'program' | 'keyboard' | 'mouse' | 'touch' = 'program', options?: FocusOptions): void {
    if (origin) {
      this._focusMonitor.focusVia(this._elementRef.nativeElement, origin, options);
    } else {
      this._elementRef.nativeElement.focus(options);
    }
  }
}
